import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, ChevronLeft, Lock, Eye, FileText, Mail, Building } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LEGAL_UPDATED_LABEL, LEGAL_VERSION } from '@/legal/version';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream-paper py-8 sm:py-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="text-forest-ink hover:text-forest-shadow hover:bg-keylime-wash mb-6"
          onClick={() => navigate('/')}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 px-2">
          <Badge className="mb-3 sm:mb-4 bg-keylime-wash text-charcoal border-border-mist px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm font-semibold">
            <Shield className="h-3 w-3 mr-1.5 inline" />
            Data Protection
          </Badge>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-forest-ink mb-3 sm:mb-4 break-words">
            Privacy Policy
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-charcoal max-w-3xl mx-auto leading-relaxed">
            Last Updated: {LEGAL_UPDATED_LABEL} (version {LEGAL_VERSION})
          </p>
        </div>

        {/* Content */}
        <Card className="bg-keylime-wash border border-border-mist">
          <CardContent className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
            <p className="text-charcoal text-sm sm:text-base md:text-lg leading-relaxed break-words">
              This Privacy Policy explains how PraesidioCare Private Limited ("ByOnco", "we", "our", or "us") collects,
              uses, stores, and protects personal and medical information ("Personal Information") when you interact
              with our services. This applies to our websites, ByOnco mobile and web apps, and any related services,
              including our cancer care navigation platform ("Services"). By using our Services, you consent to
              the terms outlined in this Privacy Policy. A separate{' '}
              <a href="/health-privacy" className="underline">Consumer Health Data Privacy Policy</a>{' '}
              covers Washington, Nevada, and Connecticut consumer health data rules.
            </p>

            <h2 className="text-xl sm:text-2xl md:text-3xl font-light text-forest-ink mb-3 sm:mb-4 flex items-center gap-2 break-words">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-forest-ink flex-shrink-0" />
              <span>1. Information We Collect</span>
            </h2>
            
            <h3 className="text-lg sm:text-xl font-semibold text-charcoal mb-2 sm:mb-3 mt-4 sm:mt-6">A. Information You Provide</h3>
            <ul className="list-disc ml-4 sm:ml-6 mb-4 sm:mb-6 space-y-1.5 sm:space-y-2 text-charcoal text-sm sm:text-base">
              <li><strong className="text-forest-ink">Account Details</strong>: Full name, phone number, email, city, and basic profile data.</li>
              <li><strong className="text-forest-ink">Patient Data</strong>: Cancer stage, treatment details, hospital preferences, financial needs, and other health-related inputs.</li>
              <li><strong className="text-forest-ink">Communications</strong>: When you contact us via WhatsApp, chat, email, or form submissions.</li>
              <li><strong className="text-forest-ink">Payment Details</strong>: Where applicable, details like cardholder name, masked card information, billing address.</li>
            </ul>

            <h3 className="text-xl font-semibold text-charcoal mb-3">B. Information We Collect Automatically</h3>
            <ul className="list-disc ml-6 mb-6 space-y-2 text-charcoal">
              <li><strong className="text-forest-ink">Usage Data</strong>: IP address, device type, browser, OS, screen resolution, time spent on app or pages.</li>
              <li><strong className="text-forest-ink">Cookies and Local Storage</strong>: To personalize experience, retain session data, and offer relevant content.</li>
              <li><strong className="text-forest-ink">Device & Diagnostic Logs</strong>: For security, troubleshooting, and improving app experience.</li>
              <li><strong className="text-forest-ink">Security Fingerprinting</strong>: Browser characteristics, device identifiers, and request patterns for abuse detection and security monitoring.</li>
            </ul>

            <h2 className="text-2xl sm:text-3xl font-light text-forest-ink mb-4 flex items-center gap-2 mt-8">
              <Eye className="h-6 w-6 text-forest-ink" />
              2. How We Use Your Information
            </h2>
            <ul className="list-disc ml-6 mb-6 space-y-2 text-charcoal">
              <li><strong className="text-forest-ink">Hospital Matching & Care Navigation</strong>: To help recommend best-fit cancer hospitals based on your profile.</li>
              <li><strong className="text-forest-ink">Communications</strong>: To send confirmations, alerts, WhatsApp messages, and respond to queries.</li>
              <li><strong className="text-forest-ink">WhatsApp Integration</strong>: When you contact us via WhatsApp, we process your messages, phone number, and related data to provide customer support, send appointment reminders, and facilitate care coordination. WhatsApp data is subject to WhatsApp's privacy policy, and we store only necessary information for service delivery.</li>
              <li><strong className="text-forest-ink">Service Optimization</strong>: Analyze aggregated data to improve user experience, routes, and performance.</li>
              <li><strong className="text-forest-ink">Personalization</strong>: Tailor hospital and treatment suggestions based on user patterns.</li>
              <li><strong className="text-forest-ink">Security & Abuse Prevention</strong>: Monitor for unauthorized access, scraping attempts, and security threats to protect our platform and users.</li>
              <li><strong className="text-forest-ink">Compliance</strong>: Ensure adherence to Indian laws like IT Act, DISHA guidelines, and data protection frameworks.</li>
            </ul>

            <h2 className="text-2xl sm:text-3xl font-light text-forest-ink mb-4 flex items-center gap-2 mt-8">
              <Lock className="h-6 w-6 text-forest-ink" />
              3. Data Sharing & Disclosure
            </h2>
            <p className="text-charcoal mb-4">We do <strong className="text-forest-ink">not</strong> sell your personal data. We only share:</p>
            <ul className="list-disc ml-6 mb-6 space-y-2 text-charcoal">
              <li><strong className="text-forest-ink">With Healthcare Partners</strong>: Hospitals and labs you engage with (only post-consent).</li>
              <li><strong className="text-forest-ink">With Trusted Vendors</strong>: Including cloud storage providers, payment gateways, and analytics services.</li>
              <li><strong className="text-forest-ink">For Legal Reasons</strong>: If required by Indian law enforcement or judiciary.</li>
              <li><strong className="text-forest-ink">Business Transfers</strong>: If ByOnco undergoes merger or acquisition.</li>
            </ul>

            <h2 className="text-2xl sm:text-3xl font-light text-forest-ink mb-4 flex items-center gap-2 mt-8">
              <Shield className="h-6 w-6 text-forest-ink" />
              4. Data Security Measures & Storage
            </h2>
            <ul className="list-disc ml-6 mb-6 space-y-2 text-charcoal">
              <li><strong className="text-forest-ink">Encryption</strong>: HTTPS/TLS for data in transit. AES encryption for data at rest.</li>
              <li><strong className="text-forest-ink">Data Storage</strong>: Your data is stored on secure cloud servers with geographic redundancy. Primary storage is in India, with backup systems as needed for service continuity.</li>
              <li><strong className="text-forest-ink">Access Control</strong>: Only essential staff and systems have access based on role. All access is logged and monitored.</li>
              <li><strong className="text-forest-ink">Audit Logs</strong>: All critical actions and logins are tracked securely.</li>
              <li><strong className="text-forest-ink">Security Monitoring</strong>: We employ automated systems to detect and prevent unauthorized access, scraping, and abuse.</li>
              <li><strong className="text-forest-ink">Breach Notification</strong>: In the event of a data breach, we'll notify affected parties as required under Indian law within 72 hours of discovery.</li>
            </ul>

            <h2 className="text-2xl sm:text-3xl font-light text-forest-ink mb-4 mt-8">5. Data Retention</h2>
            <p className="text-charcoal mb-6 leading-relaxed">
              We retain user information for as long as necessary to:
              <br />• Provide services
              <br />• Comply with medical or regulatory obligations (e.g., Ayushman Bharat scheme records)
              <br />• Improve platform accuracy
              <br />After that, data is either anonymized or deleted securely.
            </p>

            <h2 className="text-2xl sm:text-3xl font-light text-forest-ink mb-4 mt-8">6. Your Rights</h2>
            <ul className="list-disc ml-6 mb-6 space-y-2 text-charcoal">
              <li><strong className="text-forest-ink">Access</strong> to your stored information</li>
              <li><strong className="text-forest-ink">Rectification</strong> of inaccurate details</li>
              <li><strong className="text-forest-ink">Deletion</strong>, subject to legal limitations</li>
              <li><strong className="text-forest-ink">Data Portability</strong> (where feasible)</li>
              <li><strong className="text-forest-ink">Opt-out</strong> of marketing or communications via <a className="text-forest-ink hover:text-charcoal hover:underline" href="mailto:care@getbyonco.com">care@getbyonco.com</a></li>
            </ul>

            <h2 className="text-2xl sm:text-3xl font-light text-forest-ink mb-4 mt-8">7. User Consent & Compliance</h2>
            <p className="text-charcoal mb-4 leading-relaxed">
              By using ByOnco, you explicitly consent to:
            </p>
            <ul className="list-disc ml-6 mb-4 space-y-2 text-charcoal">
              <li>Processing of your health-related data for service delivery</li>
              <li>Storage of your data on secure servers as described in this policy</li>
              <li>Communication via WhatsApp, email, and other channels for service-related purposes</li>
              <li>Sharing necessary information with healthcare partners you engage with</li>
              <li>Security monitoring and abuse prevention measures</li>
            </ul>
            <p className="text-charcoal mb-4 leading-relaxed">
              You confirm that:
              <br />• You are either the patient or an authorized caregiver with legal authority to consent
              <br />• The information you provide is accurate and complete
              <br />• You agree to abide by the Terms and Conditions and Privacy Policy
            </p>
            <p className="text-charcoal mb-6 leading-relaxed">
              We comply with India's evolving data laws, including those under the <strong className="text-forest-ink">Digital Personal Data Protection Act, 2023</strong> and healthcare-specific norms. For users in the United States, we also consider applicable US privacy frameworks, though our primary jurisdiction is India.
            </p>

            <h2 className="text-2xl sm:text-3xl font-light text-forest-ink mb-4 mt-8">8. Prohibited Activities & Content Protection</h2>
            <p className="text-charcoal mb-4 leading-relaxed">
              ByOnco's content, data, and platform are protected by intellectual property laws and security measures. You agree not to:
            </p>
            <ul className="list-disc ml-6 mb-6 space-y-2 text-charcoal">
              <li><strong className="text-forest-ink">Scrape or Extract Data</strong>: Use automated tools, bots, or scripts to extract, copy, or download content, hospital data, pricing information, or user data from our platform.</li>
              <li><strong className="text-forest-ink">Redistribute Content</strong>: Republish, resell, or redistribute any content, data, or information obtained from ByOnco without written permission.</li>
              <li><strong className="text-forest-ink">Reverse Engineer</strong>: Attempt to reverse engineer, decompile, or extract source code, algorithms, or proprietary logic.</li>
              <li><strong className="text-forest-ink">Circumvent Security</strong>: Bypass security measures, rate limits, or access controls.</li>
            </ul>
            <p className="text-charcoal mb-6 leading-relaxed">
              Violations may result in immediate termination of access and legal action. We monitor for such activities and reserve the right to take appropriate measures to protect our platform and users.
            </p>

            <h2 className="text-2xl sm:text-3xl font-light text-forest-ink mb-4 mt-8">9. Changes to this Privacy Policy</h2>
            <p className="text-charcoal mb-6 leading-relaxed">
              We may revise this policy periodically. Any changes will be posted on this page with a new effective date.
              Continued use of ByOnco after updates constitutes your consent to the changes.
            </p>

            <h2 className="text-2xl sm:text-3xl font-light text-forest-ink mb-4 mt-8 flex items-center gap-2">
              <Mail className="h-6 w-6 text-forest-ink" />
              10. Contact Us
            </h2>
            <div className="text-charcoal space-y-2">
              <p><strong className="text-forest-ink">Email</strong>: <a href="mailto:care@getbyonco.com" className="text-forest-ink hover:text-charcoal hover:underline">care@getbyonco.com</a></p>
              <p className="flex items-center gap-2">
                <Building className="h-4 w-4 text-forest-ink" />
                <strong className="text-forest-ink">Registered Office</strong>: PraesidioCare Private Limited, Nashik, Maharashtra, India
              </p>
            </div>

            <div className="mt-10 text-lg italic text-charcoal text-center border-t border-border-mist pt-8">
              ByOnco, Built by people who understand your pain. Powered by people who can solve it.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
